import { ReportChapterObject } from '../../base';
import * as CaseStudies from '../../content/case-study';
import { createScores } from '../../base/scoreHelper';

export const improvementAreas: Omit<ReportChapterObject, 'number'> = {
    id: 'improvement-areas',
    caption: `Improvement areas`,
    title: `Effective GenAI requires adoption support, trusted use, and smart engineering choices`,
    description: `Successfully adopting GenAI in software development requires deliberate strategy beyond just providing tools. Key improvement areas include structured enablement programs to boost adoption, careful vendor selection balancing features with security, maintaining human oversight for legacy code and quality control, and implementing robust privacy measures. Addressing AI-specific challenges like hallucinations, operational constraints, migration complexities, and prompt engineering is also crucial for realizing productivity gains and mitigating risks.`,
    editedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
    sections: [
        {
            id: '1',
            title: `Adoption Requires Deliberate Strategy Beyond Tool Access`,
            caption: `Adoption Strategy`,
            content: [
                {
                    kind: 'text',
                    text: `While the results from AI Code Generation tools are very promising, interviews we conducted with software engineers at enterprises revealed an important finding: even in companies that have rolled out these tools, only up to 50% of potential users actually adopt them. In most companies, there’s no real effort to increase this number—instead, they’re just hoping for adoption.`,
                },
                {
                    kind: 'text',
                    text: `One company, a publicly traded data company, said they’ve seen such productivity gains from Cursor that they dedicated a two-week sprint cycle with no deliverables, where developers are required to build things using AI Code Generation tools and learn how to use them effectively. Developers who are still skeptical or don’t adopt the tools after that sprint will likely be let go.`,
                },
                {
                    kind: 'text',
                    text: `Another approach is to provide clear guidance on how to use AI Code Generation tools efficiently. You can use Varos to support this by (1) identifying the most impactful use cases for AI and demonstrating them, and (2) finding high performers with GenAI within the organization through Varos’ internal interviews and having them teach courses.`,
                },
                {
                    kind: 'text',
                    text: `Finally, you can increase the KPIs expected of developers by 25%, so they’ll need to use AI tools to hit those goals.`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.ciscoCaseStudy, CaseStudies.tiktokCaseStudy],
        },
        {
            id: '2',
            title: `Vendor Selection Trends Conservative Despite Startup Innovation`,
            caption: `Vendor Choice`,
            content: [
                {
                    kind: 'text',
                    text: `Despite there being a tsunami of new AI companies and incumbents adding AI into their products, most companies are only using GitHub Co-pilot, Gemini, and sometimes Cursor. This is surprising, as public perception is that there are many startups with more point solutions—such as automating QA testing, tracking and fixing bugs, error observability, etc.—that are being widely adopted.`,
                },
                {
                    kind: 'text',
                    text: `Two reasons cited for this are: data privacy, meaning there’s concern about giving an LLM access to the entire codebase or private data; and the fact that the “build” option in “buy vs. build” has become a lot better. Building POCs has become much easier using AI tools, and often teams choose to do that instead of going through long procurement and security processes.`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.tiktokCaseStudy],
        },
        {
            id: '3',
            title: `Legacy Code Modernization with AI Requires Human Oversight`,
            caption: `Legacy Code`,
            content: [
                {
                    kind: 'text',
                    text: `AI’s effectiveness is reduced when applied to legacy code, where incomplete documentation and a lack of comprehensive context require extensive human oversight and rigorous testing. While AI can suggest improvements, fully automating the refactoring of legacy systems remains a challenge.`,
                },
                {
                    kind: 'text',
                    text: `“Auto-refactoring legacy code with AI might give you a quick suggestion, but these old codebases are riddled with hidden dependencies and outdated patterns. Without thorough human oversight, an automated change can introduce more issues than it resolves.”`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.tiktokCaseStudy],
        },
        {
            id: '4',
            title: `Quality Control is Non-Negotiable: AI as Assistant, Not Replacement`,
            caption: `Quality Control`,
            content: [
                {
                    kind: 'text',
                    text: `Successful implementations treat AI as an assistant, not a replacement.`,
                },
                {
                    kind: 'text',
                    text: `“I treat AI like my junior engineer—he writes me a lot of code but I also need to give a lot of instructions and rigorous testing.”`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.ciscoCaseStudy],
        },
        {
            id: '5',
            title: `Data Privacy Drives Implementation Choices and Architecture`,
            caption: `Data Privacy`,
            content: [
                {
                    kind: 'text',
                    text: `Security concerns often dictate architecture and vendor decisions, leading enterprises to take approaches such as building in-house versions of popular tools, restricting tools to specific environments or users, and deploying open-source models on-premises. “We rebuilt our own version of Cursor because we needed a tool that could deeply understand our code without compromising security. With our in-house solution, we maintain full control over the code context and protect sensitive data, which external models simply can’t guarantee.”`,
                },
            ],
            keywords: [],
            sources: [
                CaseStudies.goldmanSachsCaseStudy,
                CaseStudies.airbnbCaseStudy,
                CaseStudies.tiktokCaseStudy,
                CaseStudies.capgeminiCaseStudy,
            ],
        },
        {
            id: '6',
            title: `Measuring ROI Beyond Code Volume Focuses on Broader Impact`,
            caption: `Measuring Impact`,
            content: [
                {
                    kind: 'text',
                    text: `The most successful adopters measure impact through improvements in developer productivity, time saved on routine tasks, reduction in technical debt, and the ability to focus on innovation.`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.slackCaseStudy],
        },
        {
            id: 'ai-hallucinations',
            title: `AI Hallucinations Require Intentional Mitigation Strategies`,
            caption: `Hallucinations`,
            content: [
                {
                    kind: 'text',
                    text: `Generative AI models can produce convincing but incorrect outputs (“hallucinations”), posing a significant risk to accuracy and trustworthiness. Explicit strategies are required to mitigate this risk, such as carefully structuring prompts and referencing authoritative sources.`,
                },
                {
                    kind: 'text',
                    text: `Slack’s engineering team highlighted that hallucinations were a major challenge when implementing their generative AI-based assistant. They specifically altered their prompt structures to include “sub-context” and mandatory citation of internal documentation URLs, significantly reducing misleading or incorrect AI outputs.`,
                },
                {
                    kind: 'text',
                    text: `By embedding authoritative references directly into prompts, Slack enforced verification and traceability in the AI-generated answers, greatly enhancing their reliability. “To reduce hallucinations, we changed the way we sent prompts to the LLM… and return the source url to cite the answer.”`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.uberGenieCaseStudy],
        },
        {
            id: 'operational-constraints',
            title: `Operational Constraints Limit Direct AI Deployment in Production`,
            caption: `Production Limits`,
            content: [
                {
                    kind: 'text',
                    text: `Integrating generative AI directly into production environments presents unique challenges due to operational limitations and heightened data sensitivity concerns. As a result, AI is primarily leveraged as a diagnostic support tool rather than a direct automation mechanism in live systems.`,
                },
                {
                    kind: 'text',
                    text: `Engineers found practical barriers to deploying generative AI in real-time production contexts. Despite AI’s strong diagnostic capabilities, operational constraints—such as data security requirements and system stability—limited its use to indirect roles. For instance, integration into automated ticketing through Jira proved beneficial, reducing manual overhead and enhancing tracking, yet required careful coordination between AI-generated insights and established project management workflows.`,
                },
                {
                    kind: 'text',
                    text: `“While GenAI can assist in diagnosing issues by analyzing error logs or code snippets, its direct integration into live production environments is limited due to operational constraints and the need to safeguard sensitive data.”`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.tiktokCaseStudy],
        },
        {
            id: 'framework-differences',
            title: `Framework Differences Impact Migration Complexity and Strategy`,
            caption: `Migration Complexity`,
            content: [
                {
                    kind: 'text',
                    text: `Migrating testing frameworks is not a straightforward replacement task. Fundamental differences between frameworks necessitate tailored migration strategies to ensure original test intents and coverage remain uncompromised.`,
                },
                {
                    kind: 'text',
                    text: `Slack faced significant difficulties transitioning from Enzyme to React Testing Library (RTL) because the frameworks fundamentally differ in functionality and approach. Directly replacing Enzyme with RTL was impractical due to potential loss of test coverage. Many test files also featured intricate setups and excessive indirection, which added to the complexity.`,
                },
                {
                    kind: 'text',
                    text: `This forced Slack’s engineering team to rely on additional context-aware prompt engineering and manual intervention, highlighting the importance of maintaining careful oversight throughout the migration process. “However, because of the fundamental differences between these frameworks, we couldn’t easily swap out one for the other… we also couldn’t just delete the Enzyme files, as analysis showed this would create significant gaps in our code coverage.”`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.slackCaseStudy],
        },
        {
            id: 'prompt-complexity',
            title: `Prompt Engineering Can Introduce Complexity if Not Managed`,
            caption: `Prompt Complexity`,
            content: [
                {
                    kind: 'text',
                    text: `Although prompt engineering is critical to effectively leveraging AI models, it can sometimes introduce complexity rather than streamline processes, especially during legacy code migrations or complex transformations.`,
                },
                {
                    kind: 'text',
                    text: `Slack engineers reported fluctuating success rates when using Large Language Models (LLMs), with outputs ranging between 40–60%. Paradoxically, attempts to clarify or refine LLM outputs through detailed prompt engineering occasionally increased the overall complexity instead of simplifying it. Balancing the depth of prompt instructions became a central challenge, highlighting the need to carefully manage the complexity added by prompt engineering in large-scale AI tasks.`,
                },
                {
                    kind: 'text',
                    text: `“Engineers observed that prompt engineering sometimes added complexity rather than clarity.”`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.slackCaseStudy],
        },
    ],
};
